import React, { useState } from 'react';
import jsPDF from 'jspdf';

const conditions = [
  {
    id: 'founder-phase',
    title: "The Founder's Paradox™",
    subtitle: "Condition 1: Founder Phase",
    icon: "🏁",
    description: "Where is the founder in their evolution—asset or bottleneck?",
    questions: [
      {
        id: 'f1',
        text: "In the last six months, how often has the founder personally made or overturned decisions in the area this role will own?",
        options: [
          { value: 3, label: "Rarely or never—clear delegation exists", status: "ready" },
          { value: 2, label: "Occasionally—some oversight but generally hands-off", status: "partial" },
          { value: 1, label: "Frequently—founder is actively involved in most decisions", status: "not-ready" }
        ]
      },
      {
        id: 'f2',
        text: "Has the founder successfully handed off a significant area of responsibility to someone else before?",
        options: [
          { value: 3, label: "Yes, with documented success and clear boundaries", status: "ready" },
          { value: 2, label: "Partially—some handoffs worked, others didn't", status: "partial" },
          { value: 1, label: "No history of successful delegation at this level", status: "not-ready" }
        ]
      },
      {
        id: 'f3',
        text: "If the new executive made a significant decision the founder disagreed with (not unethical, just different), what would likely happen?",
        options: [
          { value: 3, label: "Founder would accept different approaches", status: "ready" },
          { value: 2, label: "Founder would discuss and potentially influence but not override", status: "partial" },
          { value: 1, label: "Founder would likely 'course correct' or 'guide' the decision", status: "not-ready" }
        ]
      },
      {
        id: 'f4',
        text: "How would the current leadership team describe the founder's readiness to step back?",
        options: [
          { value: 3, label: "Genuinely ready—founder has other focus areas", status: "ready" },
          { value: 2, label: "Intent is there but behavior may resurface under pressure", status: "partial" },
          { value: 1, label: "Skeptical—founder's identity is tied to this area", status: "not-ready" }
        ]
      }
    ]
  },
  {
    id: 'track-surface',
    title: "The Tire Compound Strategy™",
    subtitle: "Condition 2: Track Surface",
    icon: "🏎️",
    description: "What kind of race is this organization running—sprint, endurance, turnaround, or institutionalization?",
    questions: [
      {
        id: 't1',
        text: "Describe the pace of your organization right now:",
        options: [
          { value: 3, label: "Monaco (Sprint): High speed, resource-constrained, decisions in days", status: "sprint", compound: "C5-C6" },
          { value: 3, label: "Monza (Scale-up): Sustained velocity, building while running", status: "scale", compound: "C3-C4" },
          { value: 3, label: "Silverstone (Turnaround): Hostile conditions, sustained pressure", status: "turnaround", compound: "C1-C2" },
          { value: 3, label: "Barcelona (Institutionalization): Optimization, multi-year horizons", status: "institutional", compound: "C1-C2" }
        ]
      },
      {
        id: 't2',
        text: "How long does a significant decision typically take from identification to execution?",
        options: [
          { value: 3, label: "Days to weeks—speed is essential", status: "ready" },
          { value: 2, label: "Weeks to a month—balanced process", status: "partial" },
          { value: 1, label: "Months—deliberate, consensus-driven", status: "not-ready" }
        ]
      },
      {
        id: 't3',
        text: "Is your organization primarily building new things, optimizing existing things, or fixing broken things?",
        options: [
          { value: 3, label: "Building—zero to one, greenfield work", status: "building" },
          { value: 3, label: "Optimizing—improving what works", status: "optimizing" },
          { value: 3, label: "Fixing—turnaround and transformation required", status: "fixing" }
        ]
      },
      {
        id: 't4',
        text: "What's the tolerance here for imperfect execution in pursuit of speed?",
        options: [
          { value: 3, label: "High—speed wins, we iterate and fix", status: "ready" },
          { value: 2, label: "Moderate—some room for imperfection", status: "partial" },
          { value: 1, label: "Low—we need to get it right the first time", status: "not-ready" }
        ]
      }
    ]
  },
  {
    id: 'boundary-architecture',
    title: "The Track Limits Principle™",
    subtitle: "Condition 3: Boundary Architecture",
    icon: "🚧",
    description: "Are the rules of engagement clear enough for leaders to perform at the edge?",
    questions: [
      {
        id: 'b1',
        text: "What's the largest expenditure this person could approve without additional sign-off?",
        options: [
          { value: 3, label: "Clear dollar thresholds are documented and respected", status: "ready" },
          { value: 2, label: "Informal understanding exists but not documented", status: "partial" },
          { value: 1, label: "Unclear—everything feels like it needs approval", status: "not-ready" }
        ]
      },
      {
        id: 'b2',
        text: "How clearly are boundaries defined between this role and adjacent roles (CEO, founder, board, other C-suite)?",
        options: [
          { value: 3, label: "Documented decision rights with clear swim lanes", status: "ready" },
          { value: 2, label: "Generally understood but negotiated in practice", status: "partial" },
          { value: 1, label: "Ambiguous—'we'll figure it out as we go'", status: "not-ready" }
        ]
      },
      {
        id: 'b3',
        text: "If the new executive wanted to restructure their team or exit an underperformer, what would happen?",
        options: [
          { value: 3, label: "They have clear authority to make people decisions", status: "ready" },
          { value: 2, label: "They'd need to consult but decision is theirs", status: "partial" },
          { value: 1, label: "Hiring/firing decisions require multiple approvals", status: "not-ready" }
        ]
      },
      {
        id: 'b4',
        text: "When an executive makes a decision within their stated authority that turns out wrong (but not catastrophic), what typically happens?",
        options: [
          { value: 3, label: "Treated as learning—judgment is respected", status: "ready" },
          { value: 2, label: "Discussion happens but no intervention", status: "partial" },
          { value: 1, label: "Founder/board steps in on any suboptimal outcome", status: "not-ready" }
        ]
      }
    ]
  },
  {
    id: 'transition-readiness',
    title: "The Stewardship Protocol™",
    subtitle: "Condition 4: Transition Readiness",
    icon: "🔄",
    description: "Is this organization prepared to evolve beyond its current leadership?",
    questions: [
      {
        id: 's1',
        text: "Tell me about the last external executive hire at this level. What happened?",
        options: [
          { value: 3, label: "Successful integration—they're still thriving", status: "ready" },
          { value: 2, label: "Mixed results—some worked out, some didn't", status: "partial" },
          { value: 1, label: "'External hires don't usually work out here'", status: "not-ready" }
        ]
      },
      {
        id: 's2',
        text: "Who on the current team is most likely to see this new hire as a threat, and how will that be managed?",
        options: [
          { value: 3, label: "We've identified potential friction and have a plan", status: "ready" },
          { value: 2, label: "Some awareness but no specific plan", status: "partial" },
          { value: 1, label: "Surprised by this question—haven't thought about it", status: "not-ready" }
        ]
      },
      {
        id: 's3',
        text: "What onboarding or integration support will this executive receive?",
        options: [
          { value: 3, label: "Structured 90-day program with named owner", status: "ready" },
          { value: 2, label: "Some support planned but informal", status: "partial" },
          { value: 1, label: "'They'll figure it out—they're senior'", status: "not-ready" }
        ]
      },
      {
        id: 's4',
        text: "If the new executive struggles in their first 90 days, what's the organization's instinct?",
        options: [
          { value: 3, label: "Support them through it—integration takes time", status: "ready" },
          { value: 2, label: "Some patience but questions will arise quickly", status: "partial" },
          { value: 1, label: "Question the hire—we expect immediate performance", status: "not-ready" }
        ]
      }
    ]
  },
  {
    id: 'governance-reality',
    title: "The Director Telemetry™",
    subtitle: "Condition 5: Governance Reality",
    icon: "📊",
    description: "What actually happens at the board level beneath the credentials?",
    questions: [
      {
        id: 'g1',
        text: "How does the board engage with management on strategic issues?",
        options: [
          { value: 3, label: "Strategic partnership—adds value without friction", status: "ready" },
          { value: 2, label: "Functional but requires navigation", status: "partial" },
          { value: 1, label: "Either rubber-stamp passivity or micromanagement", status: "not-ready" }
        ]
      },
      {
        id: 'g2',
        text: "When the board disagrees with management on a strategic issue, how is it resolved?",
        options: [
          { value: 3, label: "Healthy debate leading to better decisions", status: "ready" },
          { value: 2, label: "Negotiation that eventually reaches consensus", status: "partial" },
          { value: 1, label: "Either management caves or board overrides", status: "not-ready" }
        ]
      },
      {
        id: 'g3',
        text: "Does the board have relevant experience for the company's current challenges?",
        options: [
          { value: 3, label: "Directors with direct relevant expertise", status: "ready" },
          { value: 2, label: "Mixed—some relevant, some legacy appointments", status: "partial" },
          { value: 1, label: "Friends/early investors without relevant expertise", status: "not-ready" }
        ]
      },
      {
        id: 'g4',
        text: "If the new executive presents a strategy the board doesn't like, what happens?",
        options: [
          { value: 3, label: "A conversation—board as strategic partner", status: "ready" },
          { value: 2, label: "A negotiation—give and take expected", status: "partial" },
          { value: 1, label: "A veto—board approval is everything", status: "not-ready" }
        ]
      }
    ]
  }
];

// ⚠️ IMPORTANT: Replace this URL with your Google Apps Script Web App URL
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxBuopEqGvDI7fDFOQEWPpCHyeH2fB-Kvdnz0J4vrgWNQuW3iGNKCVTxAaRixqzleeN/exec';

const getStatusColor = (status) => {
  switch(status) {
    case 'ready': return '#00D4AA';
    case 'partial': return '#FFB800';
    case 'not-ready': return '#FF4444';
    default: return '#6B7280';
  }
};

const getStatusLabel = (status) => {
  switch(status) {
    case 'ready': return 'Ready';
    case 'partial': return 'Partially Ready';
    case 'not-ready': return 'Not Ready';
    default: return 'Not Assessed';
  }
};

const calculateConditionStatus = (answers, conditionId) => {
  const conditionQuestions = conditions.find(c => c.id === conditionId)?.questions || [];
  const relevantAnswers = conditionQuestions.map(q => answers[q.id]).filter(Boolean);
  
  if (relevantAnswers.length === 0) return null;
  
  const avgScore = relevantAnswers.reduce((sum, a) => sum + a.value, 0) / relevantAnswers.length;
  
  if (avgScore >= 2.5) return 'ready';
  if (avgScore >= 1.5) return 'partial';
  return 'not-ready';
};

// Shared input styles
const inputStyle = {
  width: '100%',
  padding: '14px 16px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: '#fff',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s ease'
};

const labelStyle = {
  display: 'block',
  fontSize: '11px',
  color: '#64748b',
  marginBottom: '8px',
  letterSpacing: '1px',
  textTransform: 'uppercase'
};

const RaceConditionsDiagnostic = () => {
  const [currentView, setCurrentView] = useState('intro');
  const [currentCondition, setCurrentCondition] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Client Information Fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [roleDiscussed, setRoleDiscussed] = useState('');

  const handleAnswer = (questionId, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
  };

  const nextCondition = () => {
    setAnimating(true);
    setTimeout(() => {
      if (currentCondition < conditions.length - 1) {
        setCurrentCondition(prev => prev + 1);
      } else {
        setShowResults(true);
        submitToGoogleSheets();
      }
      setAnimating(false);
    }, 300);
  };

  const prevCondition = () => {
    setAnimating(true);
    setTimeout(() => {
      setCurrentCondition(prev => Math.max(0, prev - 1));
      setAnimating(false);
    }, 300);
  };

  const getOverallAssessment = () => {
    const statuses = conditions.map(c => calculateConditionStatus(answers, c.id));
    const validStatuses = statuses.filter(Boolean);
    
    if (validStatuses.length === 0) return null;
    
    const readyCount = validStatuses.filter(s => s === 'ready').length;
    const notReadyCount = validStatuses.filter(s => s === 'not-ready').length;
    
    if (notReadyCount >= 2) return 'pause';
    if (readyCount >= 4) return 'proceed';
    return 'proceed-with-conditions';
  };

  const isConditionComplete = (conditionId) => {
    const condition = conditions.find(c => c.id === conditionId);
    return condition?.questions.every(q => answers[q.id]) || false;
  };

  const progress = conditions.filter(c => isConditionComplete(c.id)).length / conditions.length * 100;

  const submitToGoogleSheets = async () => {
    if (GOOGLE_SCRIPT_URL === 'YOUR_GOOGLE_SCRIPT_URL_HERE') {
      console.log('Google Sheets integration not configured. Skipping submission.');
      return;
    }

    setIsSubmitting(true);

    const overallStatus = getOverallAssessment();
    
    const conditionResults = {};
    conditions.forEach(condition => {
      const status = calculateConditionStatus(answers, condition.id);
      conditionResults[condition.id] = {
        status: getStatusLabel(status),
        answers: condition.questions.map(q => ({
          question: q.text,
          answer: answers[q.id]?.label || 'Not answered'
        }))
      };
    });

    const payload = {
      timestamp: new Date().toISOString(),
      firstName,
      lastName,
      email,
      phone,
      title,
      company,
      roleDiscussed,
      overallAssessment: overallStatus === 'proceed' ? 'Proceed' : 
                         overallStatus === 'pause' ? 'Pause Recommended' : 'Proceed with Conditions',
      founderPhase: getStatusLabel(calculateConditionStatus(answers, 'founder-phase')),
      trackSurface: getStatusLabel(calculateConditionStatus(answers, 'track-surface')),
      boundaryArchitecture: getStatusLabel(calculateConditionStatus(answers, 'boundary-architecture')),
      transitionReadiness: getStatusLabel(calculateConditionStatus(answers, 'transition-readiness')),
      governanceReality: getStatusLabel(calculateConditionStatus(answers, 'governance-reality')),
      detailedAnswers: JSON.stringify(conditionResults)
    };

    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      console.log('Successfully submitted to Google Sheets');
    } catch (error) {
      console.error('Error submitting to Google Sheets:', error);
    }
    
    setIsSubmitting(false);
  };

  const generatePDF = () => {
    setIsGeneratingPDF(true);
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPos = margin;
      
      const overallStatus = getOverallAssessment();
      const today = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });

      const orange = [255, 107, 0];
      const darkBlue = [26, 31, 54];
      const green = [0, 212, 170];
      const yellow = [255, 184, 0];
      const red = [255, 68, 68];
      const gray = [100, 116, 139];

      const addWrappedText = (text, x, y, maxWidth, lineHeight = 6) => {
        const lines = doc.splitTextToSize(text, maxWidth);
        lines.forEach((line, i) => {
          doc.text(line, x, y + (i * lineHeight));
        });
        return y + (lines.length * lineHeight);
      };

      doc.setFillColor(...darkBlue);
      doc.rect(0, 0, pageWidth, 45, 'F');
      
      doc.setFillColor(...orange);
      doc.rect(margin, 12, 4, 25, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('CHARLIE SOLORZANO | ALDER KOTEN', margin + 10, 18);
      
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('The Race Conditions Diagnostic™', margin + 10, 30);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(today, pageWidth - margin, 18, { align: 'right' });
      
      yPos = 55;

      const hasClientInfo = firstName || company;
      if (hasClientInfo) {
        doc.setFillColor(245, 247, 250);
        doc.rect(margin, yPos - 5, pageWidth - (margin * 2), 35, 'F');
        
        doc.setTextColor(...darkBlue);
        doc.setFontSize(9);
        
        let infoY = yPos + 3;
        
        if (firstName || lastName) {
          doc.setFont('helvetica', 'bold');
          doc.text('Contact:', margin + 5, infoY);
          doc.setFont('helvetica', 'normal');
          doc.text(`${firstName} ${lastName}`.trim(), margin + 28, infoY);
        }
        if (title) {
          doc.setFont('helvetica', 'bold');
          doc.text('Title:', pageWidth/2, infoY);
          doc.setFont('helvetica', 'normal');
          doc.text(title, pageWidth/2 + 18, infoY);
        }
        
        infoY += 10;
        
        if (company) {
          doc.setFont('helvetica', 'bold');
          doc.text('Company:', margin + 5, infoY);
          doc.setFont('helvetica', 'normal');
          doc.text(company, margin + 32, infoY);
        }
        if (roleDiscussed) {
          doc.setFont('helvetica', 'bold');
          doc.text('Role:', pageWidth/2, infoY);
          doc.setFont('helvetica', 'normal');
          doc.text(roleDiscussed, pageWidth/2 + 18, infoY);
        }
        
        infoY += 10;
        
        if (email) {
          doc.setFont('helvetica', 'bold');
          doc.text('Email:', margin + 5, infoY);
          doc.setFont('helvetica', 'normal');
          doc.text(email, margin + 24, infoY);
        }
        if (phone) {
          doc.setFont('helvetica', 'bold');
          doc.text('Phone:', pageWidth/2, infoY);
          doc.setFont('helvetica', 'normal');
          doc.text(phone, pageWidth/2 + 22, infoY);
        }
        
        yPos += 40;
      }

      const statusColor = overallStatus === 'proceed' ? green : 
                         overallStatus === 'pause' ? red : yellow;
      
      doc.setDrawColor(...statusColor);
      doc.setLineWidth(1.5);
      doc.setFillColor(statusColor[0], statusColor[1], statusColor[2], 0.1);
      doc.roundedRect(margin, yPos, pageWidth - (margin * 2), 45, 3, 3, 'FD');
      
      doc.setTextColor(...statusColor);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('OVERALL ASSESSMENT', margin + 10, yPos + 12);
      
      doc.setTextColor(...darkBlue);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      const statusText = overallStatus === 'proceed' ? 'PROCEED' : 
                        overallStatus === 'pause' ? 'PAUSE RECOMMENDED' : 'PROCEED WITH CONDITIONS';
      doc.text(statusText, margin + 10, yPos + 25);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...gray);
      const statusDesc = overallStatus === 'proceed' 
        ? 'Conditions support a successful hire. The organizational environment is prepared to receive and support new leadership.'
        : overallStatus === 'pause'
        ? 'Organizational work is required before a search makes sense. Addressing the identified conditions first will significantly improve the probability of success.'
        : 'The search is viable, but specific issues should be addressed. Consider including condition-specific mitigations in your onboarding plan.';
      addWrappedText(statusDesc, margin + 10, yPos + 35, pageWidth - (margin * 2) - 20, 5);
      
      yPos += 60;

      doc.setTextColor(...darkBlue);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Condition Assessment Summary', margin, yPos);
      yPos += 10;

      doc.setFillColor(...darkBlue);
      doc.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Condition', margin + 5, yPos + 7);
      doc.text('Framework', margin + 70, yPos + 7);
      doc.text('Status', pageWidth - margin - 25, yPos + 7);
      yPos += 10;

      conditions.forEach((condition, i) => {
        const status = calculateConditionStatus(answers, condition.id);
        const rowColor = i % 2 === 0 ? [255, 255, 255] : [245, 247, 250];
        
        doc.setFillColor(...rowColor);
        doc.rect(margin, yPos, pageWidth - (margin * 2), 12, 'F');
        
        doc.setTextColor(...darkBlue);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(`${i + 1}. ${condition.subtitle.split(': ')[1]}`, margin + 5, yPos + 8);
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(condition.title, margin + 70, yPos + 8);
        
        const sColor = status === 'ready' ? green : status === 'partial' ? yellow : red;
        doc.setFillColor(...sColor);
        doc.circle(pageWidth - margin - 30, yPos + 6, 2, 'F');
        doc.setTextColor(...sColor);
        doc.setFontSize(8);
        doc.text(getStatusLabel(status), pageWidth - margin - 25, yPos + 8);
        
        yPos += 12;
      });

      yPos += 15;

      doc.setTextColor(...darkBlue);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Key Findings by Condition', margin, yPos);
      yPos += 10;

      conditions.forEach((condition) => {
        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = margin;
        }

        const status = calculateConditionStatus(answers, condition.id);
        const sColor = status === 'ready' ? green : status === 'partial' ? yellow : red;
        
        doc.setFillColor(...sColor);
        doc.rect(margin, yPos, 3, 20, 'F');
        
        doc.setTextColor(...darkBlue);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(`${condition.subtitle}`, margin + 8, yPos + 6);
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...gray);
        doc.text(condition.title, margin + 8, yPos + 14);
        
        doc.setFillColor(sColor[0], sColor[1], sColor[2], 0.15);
        doc.roundedRect(pageWidth - margin - 35, yPos + 2, 35, 12, 2, 2, 'F');
        doc.setTextColor(...sColor);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(getStatusLabel(status), pageWidth - margin - 33, yPos + 10);
        
        yPos += 25;

        condition.questions.forEach((question) => {
          const answer = answers[question.id];
          if (answer) {
            if (yPos > pageHeight - 30) {
              doc.addPage();
              yPos = margin;
            }
            
            doc.setTextColor(...gray);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            const answerText = `• ${answer.label}`;
            yPos = addWrappedText(answerText, margin + 8, yPos, pageWidth - (margin * 2) - 16, 5);
            yPos += 3;
          }
        });
        
        yPos += 10;
      });

      const notReadyConditions = conditions.filter(c => calculateConditionStatus(answers, c.id) === 'not-ready');
      
      if (notReadyConditions.length > 0) {
        if (yPos > pageHeight - 80) {
          doc.addPage();
          yPos = margin;
        }

        doc.setFillColor(255, 240, 240);
        doc.rect(margin, yPos, pageWidth - (margin * 2), 10 + (notReadyConditions.length * 12), 'F');
        doc.setDrawColor(...red);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, margin, yPos + 10 + (notReadyConditions.length * 12));
        
        doc.setTextColor(...red);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Key Risks Identified', margin + 5, yPos + 8);
        yPos += 15;
        
        notReadyConditions.forEach((c, i) => {
          doc.setTextColor(...darkBlue);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.text(`${i + 1}. ${c.title}: ${c.description}`, margin + 5, yPos);
          yPos += 10;
        });
      }

      const footerY = pageHeight - 25;
      doc.setDrawColor(...orange);
      doc.setLineWidth(0.5);
      doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      
      doc.setTextColor(...gray);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Charlie Solorzano | Alder Koten', margin, footerY);
      doc.text('carlos.solorzano@alderkoten.com | +1 (713) 337-7943', margin, footerY + 5);
      
      doc.setTextColor(...orange);
      doc.text('charliesolorzano.me/the-race-conditions-model', pageWidth - margin, footerY, { align: 'right' });
      
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.text('CONFIDENTIAL - Not for Distribution', pageWidth - margin, footerY + 5, { align: 'right' });

      const clientName = `${firstName} ${lastName}`.trim() || company;
      const fileName = clientName 
        ? `Race-Conditions-Diagnostic-${clientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`
        : `Race-Conditions-Diagnostic-${new Date().toISOString().split('T')[0]}.pdf`;
      
      doc.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
    
    setIsGeneratingPDF(false);
  };

  const canProceed = firstName && lastName && email && company;

  // Intro Screen
  if (currentView === 'intro') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0e17 0%, #1a1f36 50%, #0d1220 100%)',
        fontFamily: "'Space Grotesk', 'Segoe UI', system-ui, sans-serif",
        color: '#fff',
        padding: '40px 20px',
        boxSizing: 'border-box'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '48px' }}>
            <div style={{
              width: '4px',
              height: '60px',
              background: 'linear-gradient(180deg, #FF6B00 0%, #FF8C40 100%)',
              borderRadius: '2px'
            }} />
            <div>
              <div style={{
                fontSize: '12px',
                letterSpacing: '3px',
                color: '#FF6B00',
                textTransform: 'uppercase',
                marginBottom: '4px'
              }}>Charlie Solórzano • Alder Koten</div>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '700',
                margin: 0,
                background: 'linear-gradient(90deg, #fff 0%, #a0aec0 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>The Race Conditions Diagnostic™</h1>
            </div>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,107,0,0.2)',
            borderRadius: '16px',
            padding: '48px',
            marginBottom: '32px'
          }}>
            <p style={{
              fontSize: '20px',
              lineHeight: '1.7',
              color: '#94a3b8',
              marginBottom: '32px'
            }}>
              Most executive hires fail not because of talent—the candidates are usually impressive. 
              <span style={{ color: '#fff', fontWeight: '500' }}> They fail because no one diagnosed the conditions </span> 
              the executive was walking into.
            </p>

            <div style={{
              background: 'rgba(255,107,0,0.05)',
              border: '1px solid rgba(255,107,0,0.15)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '32px'
            }}>
              <h3 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#FF6B00',
                marginBottom: '20px',
                letterSpacing: '1px'
              }}>YOUR INFORMATION</h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                {/* First Name */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>
                    First Name <span style={{ color: '#FF6B00' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    style={inputStyle}
                  />
                </div>

                {/* Last Name */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>
                    Last Name <span style={{ color: '#FF6B00' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Smith"
                    style={inputStyle}
                  />
                </div>

                {/* Email */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>
                    Email <span style={{ color: '#FF6B00' }}>*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@company.com"
                    style={inputStyle}
                  />
                </div>

                {/* Phone */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>WhatsApp / Phone</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    style={inputStyle}
                  />
                </div>

                {/* Title */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Your Title</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="CEO, Founder, CHRO..."
                    style={inputStyle}
                  />
                </div>

                {/* Company */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>
                    Company <span style={{ color: '#FF6B00' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    placeholder="Acme Corporation"
                    style={inputStyle}
                  />
                </div>
              </div>
              
              {/* Role Being Discussed */}
              <div style={{ marginTop: '16px' }}>
                <label style={labelStyle}>Role Being Discussed</label>
                <input
                  type="text"
                  value={roleDiscussed}
                  onChange={(e) => setRoleDiscussed(e.target.value)}
                  placeholder="e.g., CFO, COO, VP of Sales"
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px',
              marginBottom: '40px'
            }}>
              {conditions.map((condition, i) => (
                <div key={condition.id} style={{
                  background: 'rgba(255,107,0,0.05)',
                  border: '1px solid rgba(255,107,0,0.15)',
                  borderRadius: '12px',
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px'
                }}>
                  <div style={{
                    fontSize: '24px',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,107,0,0.1)',
                    borderRadius: '8px'
                  }}>{condition.icon}</div>
                  <div>
                    <div style={{ fontSize: '11px', color: '#FF6B00', letterSpacing: '1px', marginBottom: '4px' }}>
                      CONDITION {i + 1}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#e2e8f0' }}>{condition.title}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              background: 'linear-gradient(90deg, rgba(255,107,0,0.1) 0%, rgba(255,107,0,0.05) 100%)',
              borderLeft: '3px solid #FF6B00',
              padding: '20px 24px',
              borderRadius: '0 8px 8px 0',
              marginBottom: '32px'
            }}>
              <p style={{ fontSize: '16px', color: '#e2e8f0', margin: 0, fontStyle: 'italic' }}>
                "Before I make any leadership placement, I diagnose these five conditions. Most searches fail not because of talent—but because no one read the race conditions first."
              </p>
            </div>

            <button
              onClick={() => canProceed && setCurrentView('assessment')}
              disabled={!canProceed}
              style={{
                width: '100%',
                padding: '20px 32px',
                fontSize: '16px',
                fontWeight: '600',
                background: canProceed ? 'linear-gradient(135deg, #FF6B00 0%, #FF8C40 100%)' : 'rgba(255,255,255,0.1)',
                color: canProceed ? '#fff' : '#64748b',
                border: 'none',
                borderRadius: '12px',
                cursor: canProceed ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                transition: 'all 0.3s ease',
                boxShadow: canProceed ? '0 4px 24px rgba(255,107,0,0.3)' : 'none'
              }}
            >
              {canProceed ? (
                <>Begin Diagnostic Assessment <span style={{ fontSize: '20px' }}>→</span></>
              ) : (
                'Please fill in required fields to continue'
              )}
            </button>
          </div>

          <p style={{ textAlign: 'center', fontSize: '13px', color: '#64748b' }}>
            This assessment takes approximately 10-15 minutes to complete
          </p>
        </div>
      </div>
    );
  }

  // Results Screen
  if (showResults) {
    const overallStatus = getOverallAssessment();
    
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0e17 0%, #1a1f36 50%, #0d1220 100%)',
        fontFamily: "'Space Grotesk', 'Segoe UI', system-ui, sans-serif",
        color: '#fff',
        padding: '40px 20px',
        boxSizing: 'border-box'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '32px',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '4px',
                height: '48px',
                background: 'linear-gradient(180deg, #FF6B00 0%, #FF8C40 100%)',
                borderRadius: '2px'
              }} />
              <div>
                <div style={{ fontSize: '12px', letterSpacing: '2px', color: '#FF6B00' }}>DIAGNOSTIC COMPLETE</div>
                <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0 }}>Race Conditions Assessment</h1>
              </div>
            </div>
            
            <button
              onClick={generatePDF}
              disabled={isGeneratingPDF}
              style={{
                padding: '14px 24px',
                background: isGeneratingPDF ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #1a1f36 0%, #2d3555 100%)',
                border: '1px solid rgba(255,107,0,0.4)',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isGeneratingPDF ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              {isGeneratingPDF ? (
                <>
                  <span style={{ 
                    width: '16px', height: '16px', 
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#FF6B00',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Generating...
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="12" y1="18" x2="12" y2="12"/>
                    <line x1="9" y1="15" x2="12" y2="18"/>
                    <line x1="15" y1="15" x2="12" y2="18"/>
                  </svg>
                  Download PDF Report
                </>
              )}
            </button>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '10px',
            padding: '16px 20px',
            marginBottom: '24px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '24px'
          }}>
            {firstName && (
              <div>
                <span style={{ fontSize: '11px', color: '#64748b', letterSpacing: '1px' }}>CONTACT: </span>
                <span style={{ fontSize: '14px', color: '#e2e8f0', fontWeight: '500' }}>
                  {firstName} {lastName}
                </span>
              </div>
            )}
            {company && (
              <div>
                <span style={{ fontSize: '11px', color: '#64748b', letterSpacing: '1px' }}>COMPANY: </span>
                <span style={{ fontSize: '14px', color: '#e2e8f0', fontWeight: '500' }}>{company}</span>
              </div>
            )}
            {roleDiscussed && (
              <div>
                <span style={{ fontSize: '11px', color: '#64748b', letterSpacing: '1px' }}>ROLE: </span>
                <span style={{ fontSize: '14px', color: '#e2e8f0', fontWeight: '500' }}>{roleDiscussed}</span>
              </div>
            )}
          </div>

          {isSubmitting && (
            <div style={{
              background: 'rgba(255,107,0,0.1)',
              border: '1px solid rgba(255,107,0,0.3)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              fontSize: '14px',
              color: '#FF6B00',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <span style={{ 
                width: '14px', height: '14px', 
                border: '2px solid rgba(255,107,0,0.3)',
                borderTopColor: '#FF6B00',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              Saving your assessment...
            </div>
          )}

          <div style={{
            background: overallStatus === 'proceed' ? 'rgba(0,212,170,0.1)' : 
                       overallStatus === 'pause' ? 'rgba(255,68,68,0.1)' : 'rgba(255,184,0,0.1)',
            border: `2px solid ${overallStatus === 'proceed' ? '#00D4AA' : 
                                overallStatus === 'pause' ? '#FF4444' : '#FFB800'}`,
            borderRadius: '16px',
            padding: '32px',
            marginBottom: '32px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '14px',
              letterSpacing: '2px',
              color: overallStatus === 'proceed' ? '#00D4AA' : overallStatus === 'pause' ? '#FF4444' : '#FFB800',
              marginBottom: '12px'
            }}>OVERALL ASSESSMENT</div>
            <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '16px' }}>
              {overallStatus === 'proceed' ? '✓ Proceed' : 
               overallStatus === 'pause' ? '⏸ Pause Recommended' : '⚠ Proceed with Conditions'}
            </div>
            <p style={{ fontSize: '16px', color: '#94a3b8', maxWidth: '600px', margin: '0 auto' }}>
              {overallStatus === 'proceed' 
                ? 'Conditions support a successful hire. The organizational environment is prepared to receive and support new leadership.'
                : overallStatus === 'pause'
                ? 'Organizational work is required before a search makes sense. Addressing the identified conditions first will significantly improve the probability of success.'
                : 'The search is viable, but specific issues should be addressed. Consider including condition-specific mitigations in your onboarding plan.'}
            </p>
          </div>

          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: '#e2e8f0' }}>
              Condition Assessment Details
            </h2>
            
            {conditions.map((condition, i) => {
              const status = calculateConditionStatus(answers, condition.id);
              return (
                <div key={condition.id} style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '20px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1, minWidth: '250px' }}>
                    <div style={{
                      fontSize: '28px',
                      width: '50px',
                      height: '50px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255,107,0,0.1)',
                      borderRadius: '10px',
                      flexShrink: 0
                    }}>{condition.icon}</div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#64748b', letterSpacing: '1px' }}>CONDITION {i + 1}</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#e2e8f0' }}>{condition.title}</div>
                      <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px' }}>{condition.description}</div>
                    </div>
                  </div>
                  <div style={{
                    padding: '10px 20px',
                    background: `${getStatusColor(status)}15`,
                    border: `1px solid ${getStatusColor(status)}40`,
                    borderRadius: '8px',
                    color: getStatusColor(status),
                    fontWeight: '600',
                    fontSize: '13px',
                    whiteSpace: 'nowrap'
                  }}>
                    {status === 'ready' ? '● Ready' : status === 'partial' ? '◐ Partial' : '○ Not Ready'}
                  </div>
                </div>
              );
            })}
          </div>

          {conditions.some(c => calculateConditionStatus(answers, c.id) === 'not-ready') && (
            <div style={{
              background: 'rgba(255,68,68,0.08)',
              border: '1px solid rgba(255,68,68,0.2)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '32px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#FF4444', marginBottom: '16px' }}>
                ⚠ Key Risks Identified
              </h3>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#e2e8f0' }}>
                {conditions.filter(c => calculateConditionStatus(answers, c.id) === 'not-ready').map(c => (
                  <li key={c.id} style={{ marginBottom: '8px' }}>
                    <strong>{c.title}:</strong> {c.description}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{
            background: 'linear-gradient(135deg, rgba(255,107,0,0.15) 0%, rgba(255,107,0,0.05) 100%)',
            border: '1px solid rgba(255,107,0,0.3)',
            borderRadius: '16px',
            padding: '32px',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '12px' }}>
              Ready to Discuss Your Conditions?
            </h3>
            <p style={{ color: '#94a3b8', marginBottom: '24px' }}>
              A 30-minute diagnostic conversation can reveal what months of interviews won't.
            </p>
            <a
              href="https://calendly.com/csolorzanoe/race-conditions-model"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '16px 32px',
                background: 'linear-gradient(135deg, #FF6B00 0%, #FF8C40 100%)',
                color: '#fff',
                fontWeight: '600',
                borderRadius: '10px',
                textDecoration: 'none',
                boxShadow: '0 4px 24px rgba(255,107,0,0.3)'
              }}
            >
              Schedule a Diagnostic Conversation →
            </a>
          </div>

          <button
            onClick={() => {
              setShowResults(false);
              setCurrentCondition(0);
              setAnswers({});
              setFirstName('');
              setLastName('');
              setEmail('');
              setPhone('');
              setTitle('');
              setCompany('');
              setRoleDiscussed('');
              setCurrentView('intro');
            }}
            style={{
              display: 'block',
              margin: '24px auto 0',
              padding: '12px 24px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '8px',
              color: '#94a3b8',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            ← Start New Assessment
          </button>
        </div>
        
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Assessment Screen
  const condition = conditions[currentCondition];
  const allQuestionsAnswered = condition.questions.every(q => answers[q.id]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e17 0%, #1a1f36 50%, #0d1220 100%)',
      fontFamily: "'Space Grotesk', 'Segoe UI', system-ui, sans-serif",
      color: '#fff',
      padding: '32px 20px',
      boxSizing: 'border-box'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', color: '#64748b', letterSpacing: '1px' }}>
              CONDITION {currentCondition + 1} OF {conditions.length}
            </span>
            <span style={{ fontSize: '12px', color: '#FF6B00' }}>{Math.round(progress)}% Complete</span>
          </div>
          <div style={{ height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #FF6B00 0%, #FF8C40 100%)',
              borderRadius: '2px',
              transition: 'width 0.5s ease'
            }} />
          </div>
          
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'center' }}>
            {conditions.map((c, i) => {
              const status = calculateConditionStatus(answers, c.id);
              const isCurrent = i === currentCondition;
              return (
                <button
                  key={c.id}
                  onClick={() => setCurrentCondition(i)}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    border: isCurrent ? '2px solid #FF6B00' : '1px solid rgba(255,255,255,0.2)',
                    background: status ? `${getStatusColor(status)}20` : 'transparent',
                    color: isCurrent ? '#FF6B00' : '#64748b',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {status ? (
                    <span style={{ color: getStatusColor(status) }}>
                      {status === 'ready' ? '✓' : status === 'partial' ? '◐' : '○'}
                    </span>
                  ) : i + 1}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,107,0,0.2)',
          borderRadius: '20px',
          padding: '40px',
          opacity: animating ? 0 : 1,
          transform: animating ? 'translateX(20px)' : 'translateX(0)',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
            <div style={{
              fontSize: '40px',
              width: '64px',
              height: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,107,0,0.1)',
              borderRadius: '16px'
            }}>{condition.icon}</div>
            <div>
              <div style={{ fontSize: '11px', letterSpacing: '2px', color: '#FF6B00', marginBottom: '4px' }}>
                {condition.subtitle.toUpperCase()}
              </div>
              <h2 style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>{condition.title}</h2>
            </div>
          </div>

          <p style={{
            fontSize: '16px',
            color: '#94a3b8',
            marginBottom: '32px',
            padding: '16px 20px',
            background: 'rgba(255,107,0,0.05)',
            borderRadius: '10px',
            borderLeft: '3px solid #FF6B00'
          }}>
            <strong style={{ color: '#e2e8f0' }}>Diagnostic question:</strong> {condition.description}
          </p>

          {condition.questions.map((question, qi) => (
            <div key={question.id} style={{ marginBottom: '32px' }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#e2e8f0',
                marginBottom: '16px',
                display: 'flex',
                gap: '12px'
              }}>
                <span style={{
                  width: '24px',
                  height: '24px',
                  background: answers[question.id] ? 'linear-gradient(135deg, #FF6B00, #FF8C40)' : 'rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  flexShrink: 0
                }}>
                  {answers[question.id] ? '✓' : qi + 1}
                </span>
                {question.text}
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {question.options.map((option, oi) => (
                  <button
                    key={oi}
                    onClick={() => handleAnswer(question.id, option)}
                    style={{
                      padding: '16px 20px',
                      background: answers[question.id] === option 
                        ? `${getStatusColor(option.status)}15`
                        : 'rgba(255,255,255,0.03)',
                      border: answers[question.id] === option
                        ? `2px solid ${getStatusColor(option.status)}`
                        : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: answers[question.id] === option ? '#fff' : '#94a3b8',
                      fontSize: '14px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                  >
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: answers[question.id] === option 
                        ? `2px solid ${getStatusColor(option.status)}`
                        : '2px solid rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {answers[question.id] === option && (
                        <span style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          background: getStatusColor(option.status)
                        }} />
                      )}
                    </span>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', gap: '16px' }}>
          <button
            onClick={prevCondition}
            disabled={currentCondition === 0}
            style={{
              padding: '14px 28px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '10px',
              color: currentCondition === 0 ? '#4a5568' : '#e2e8f0',
              fontSize: '14px',
              fontWeight: '600',
              cursor: currentCondition === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            ← Previous
          </button>
          
          <button
            onClick={nextCondition}
            disabled={!allQuestionsAnswered}
            style={{
              padding: '14px 32px',
              background: allQuestionsAnswered 
                ? 'linear-gradient(135deg, #FF6B00 0%, #FF8C40 100%)'
                : 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '10px',
              color: allQuestionsAnswered ? '#fff' : '#64748b',
              fontSize: '14px',
              fontWeight: '600',
              cursor: allQuestionsAnswered ? 'pointer' : 'not-allowed',
              boxShadow: allQuestionsAnswered ? '0 4px 20px rgba(255,107,0,0.3)' : 'none'
            }}
          >
            {currentCondition === conditions.length - 1 ? 'View Results' : 'Next Condition'} →
          </button>
        </div>
      </div>
    </div>
  );
};

export default RaceConditionsDiagnostic;
